import { createSlice } from "@reduxjs/toolkit"
import axios from "axios"

const initialState = {
    // List packages
    listPackagesLoading: false,
    listPackagesSuccess: false,
    listPackagesFail: false,
    listPackagesData: [],
    listPackagesErrorMessage: null,

    // Get package detail
    getPackageDetailLoading: false,
    getPackageDetailSuccess: false,
    getPackageDetailFail: false,
    getPackageDetailData: null,
    getPackageDetailErrorMessage: null,

    // Purchase package
    purchasePackageLoading: false,
    purchasePackageSuccess: false,
    purchasePackageFail: false,
    purchasePackageData: null,
    purchasePackageErrorMessage: null,

    // Initiate online payment (coin top-up through the payment gateway)
    initiatePaymentLoading: false,
    initiatePaymentSuccess: false,
    initiatePaymentFail: false,
    initiatePaymentData: null,
    initiatePaymentErrorMessage: null,

    // Payment methods the server offers for a top-up. "online" (FPX / e-wallet on the
    // gateway's own page) is always there; "duitnow_qr" appears only when the server
    // has DuitNow QR switched on.
    paymentMethodsLoading: false,
    paymentMethods: ["online"],
}

export const creditPackagesSlice = createSlice({
    name: "creditPackages",
    initialState,
    reducers: {
        // List Packages Actions
        listPackagesRequest: (state) => {
            state.listPackagesLoading = true
            state.listPackagesSuccess = false
            state.listPackagesFail = false
            state.listPackagesErrorMessage = null
        },
        listPackagesSuccessAction: (state, action) => {
            state.listPackagesLoading = false
            state.listPackagesSuccess = true
            state.listPackagesFail = false
            state.listPackagesData = action.payload
            state.listPackagesErrorMessage = null
        },
        listPackagesFailAction: (state, action) => {
            state.listPackagesLoading = false
            state.listPackagesSuccess = false
            state.listPackagesFail = true
            state.listPackagesErrorMessage = action.payload
        },
        resetListPackages: (state) => {
            state.listPackagesLoading = false
            state.listPackagesSuccess = false
            state.listPackagesFail = false
            state.listPackagesErrorMessage = null
        },

        // Get Package Detail Actions
        getPackageDetailRequest: (state) => {
            state.getPackageDetailLoading = true
            state.getPackageDetailSuccess = false
            state.getPackageDetailFail = false
            state.getPackageDetailErrorMessage = null
        },
        getPackageDetailSuccessAction: (state, action) => {
            state.getPackageDetailLoading = false
            state.getPackageDetailSuccess = true
            state.getPackageDetailFail = false
            state.getPackageDetailData = action.payload
            state.getPackageDetailErrorMessage = null
        },
        getPackageDetailFailAction: (state, action) => {
            state.getPackageDetailLoading = false
            state.getPackageDetailSuccess = false
            state.getPackageDetailFail = true
            state.getPackageDetailErrorMessage = action.payload
        },
        resetGetPackageDetail: (state) => {
            state.getPackageDetailLoading = false
            state.getPackageDetailSuccess = false
            state.getPackageDetailFail = false
            state.getPackageDetailData = null
            state.getPackageDetailErrorMessage = null
        },

        // Purchase Package Actions
        purchasePackageRequest: (state) => {
            state.purchasePackageLoading = true
            state.purchasePackageSuccess = false
            state.purchasePackageFail = false
            state.purchasePackageErrorMessage = null
        },
        purchasePackageSuccessAction: (state, action) => {
            state.purchasePackageLoading = false
            state.purchasePackageSuccess = true
            state.purchasePackageFail = false
            state.purchasePackageData = action.payload
            state.purchasePackageErrorMessage = null
        },
        purchasePackageFailAction: (state, action) => {
            state.purchasePackageLoading = false
            state.purchasePackageSuccess = false
            state.purchasePackageFail = true
            state.purchasePackageErrorMessage = action.payload
        },
        resetPurchasePackage: (state) => {
            state.purchasePackageLoading = false
            state.purchasePackageSuccess = false
            state.purchasePackageFail = false
            state.purchasePackageData = null
            state.purchasePackageErrorMessage = null
        },

        // Initiate Payment Actions
        initiatePaymentRequest: (state) => {
            state.initiatePaymentLoading = true
            state.initiatePaymentSuccess = false
            state.initiatePaymentFail = false
            state.initiatePaymentErrorMessage = null
        },
        initiatePaymentSuccessAction: (state, action) => {
            state.initiatePaymentLoading = false
            state.initiatePaymentSuccess = true
            state.initiatePaymentFail = false
            state.initiatePaymentData = action.payload
            state.initiatePaymentErrorMessage = null
        },
        initiatePaymentFailAction: (state, action) => {
            state.initiatePaymentLoading = false
            state.initiatePaymentSuccess = false
            state.initiatePaymentFail = true
            state.initiatePaymentErrorMessage = action.payload
        },
        resetInitiatePayment: (state) => {
            state.initiatePaymentLoading = false
            state.initiatePaymentSuccess = false
            state.initiatePaymentFail = false
            state.initiatePaymentData = null
            state.initiatePaymentErrorMessage = null
        },

        // Payment Methods Actions
        paymentMethodsRequest: (state) => {
            state.paymentMethodsLoading = true
        },
        paymentMethodsLoaded: (state, action) => {
            state.paymentMethodsLoading = false
            state.paymentMethods = action.payload
        },
    },
})

// Action creators
export const {
    listPackagesRequest,
    listPackagesSuccessAction,
    listPackagesFailAction,
    resetListPackages,
    getPackageDetailRequest,
    getPackageDetailSuccessAction,
    getPackageDetailFailAction,
    resetGetPackageDetail,
    purchasePackageRequest,
    purchasePackageSuccessAction,
    purchasePackageFailAction,
    resetPurchasePackage,
    initiatePaymentRequest,
    initiatePaymentSuccessAction,
    initiatePaymentFailAction,
    resetInitiatePayment,
    paymentMethodsRequest,
    paymentMethodsLoaded,
} = creditPackagesSlice.actions

// Selectors
export const getListPackagesLoading = (state) => state.creditPackages.listPackagesLoading
export const getListPackagesSuccess = (state) => state.creditPackages.listPackagesSuccess
export const getListPackagesFail = (state) => state.creditPackages.listPackagesFail
export const getListPackagesData = (state) => state.creditPackages.listPackagesData
export const getListPackagesErrorMessage = (state) => state.creditPackages.listPackagesErrorMessage

export const getPackageDetailLoading = (state) => state.creditPackages.getPackageDetailLoading
export const getPackageDetailSuccess = (state) => state.creditPackages.getPackageDetailSuccess
export const getPackageDetailFail = (state) => state.creditPackages.getPackageDetailFail
export const getPackageDetailData = (state) => state.creditPackages.getPackageDetailData
export const getPackageDetailErrorMessage = (state) => state.creditPackages.getPackageDetailErrorMessage

export const getPurchasePackageLoading = (state) => state.creditPackages.purchasePackageLoading
export const getPurchasePackageSuccess = (state) => state.creditPackages.purchasePackageSuccess
export const getPurchasePackageFail = (state) => state.creditPackages.purchasePackageFail
export const getPurchasePackageData = (state) => state.creditPackages.purchasePackageData
export const getPurchasePackageErrorMessage = (state) => state.creditPackages.purchasePackageErrorMessage

export const getInitiatePaymentLoading = (state) => state.creditPackages.initiatePaymentLoading
export const getInitiatePaymentSuccess = (state) => state.creditPackages.initiatePaymentSuccess
export const getInitiatePaymentFail = (state) => state.creditPackages.initiatePaymentFail
export const getInitiatePaymentData = (state) => state.creditPackages.initiatePaymentData
export const getInitiatePaymentErrorMessage = (state) => state.creditPackages.initiatePaymentErrorMessage

export const getPaymentMethods = (state) => state.creditPackages.paymentMethods
export const getPaymentMethodsLoading = (state) => state.creditPackages.paymentMethodsLoading

// Thunks (API calls)

/**
 * Get list of all credit packages
 */
export const doListCreditPackages = () => {
    return async (dispatch) => {
        dispatch(listPackagesRequest())

        try {
            const response = await axios.get(
                `/api/customer/credit-package`
            )

            // if (response.data.success) {
                dispatch(listPackagesSuccessAction(response.data.data))
            // } else {
            //     dispatch(listPackagesFailAction(response.data.message || "Failed to fetch packages"))
            // }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(listPackagesFailAction(errorMessage))
        }
    }
}

/**
 * Get package detail by ID
 * @param {string} packageId - The package ID
 */
export const doGetPackageDetail = (packageId) => {
    return async (dispatch) => {
        dispatch(getPackageDetailRequest())

        try {
            const response = await axios.get(
                `/api/customer/credit-package/${packageId}`
            )

            dispatch(getPackageDetailSuccessAction(response.data.data))
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(getPackageDetailFailAction(errorMessage))
        }
    }
}

/**
 * Purchase a credit package
 * @param {object} purchaseData - { packageId, paymentMethod, paymentDetails }
 */
export const doPurchaseCreditPackage = (purchaseData) => {
    return async (dispatch) => {
        dispatch(purchasePackageRequest())

        try {
            const response = await axios.post(
                `/api/customer/credit-packages/purchase`,
                purchaseData
            )

            if (response.data.success) {
                dispatch(purchasePackageSuccessAction(response.data.data))
            } else {
                dispatch(purchasePackageFailAction(response.data.message || "Purchase failed"))
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(purchasePackageFailAction(errorMessage))
        }
    }
}

/**
 * Initiate an online payment (through the payment gateway) for one or more credit packages.
 * Resolves to { payment_url, reference_id, amount } on success so the caller can
 * redirect the browser to the gateway's payment page; resolves to null on failure.
 * @param {object} payload - { items: [{ package_id, quantity }] } or { package_id, quantity },
 *   plus an optional payment_method: "online" | "duitnow_qr"
 */
export const doInitiatePayment = (payload) => {
    return async (dispatch) => {
        dispatch(initiatePaymentRequest())

        try {
            const response = await axios.post(
                `/api/customer/payment/initiate`,
                payload
            )

            const data = response.data.data
            dispatch(initiatePaymentSuccessAction(data))
            return data
        } catch (error) {
            const errorMessage =
                error.response?.data?.errorMessage ||
                error.response?.data?.message ||
                error.message ||
                "Network error occurred"
            dispatch(initiatePaymentFailAction(errorMessage))
            return null
        }
    }
}

const KNOWN_PAYMENT_METHODS = ["online", "duitnow_qr"]

/**
 * Ask the server which payment methods to offer on the Coin Packages page.
 * The response is { message, data: { methods: ["online"] | ["online", "duitnow_qr"] } }.
 * Any error or unexpected shape leaves just ["online"], so the page then behaves as
 * it did before the choice existed.
 */
export const doGetPaymentMethods = () => {
    return async (dispatch) => {
        dispatch(paymentMethodsRequest())

        let methods = ["online"]
        try {
            const response = await axios.get(`/api/customer/payment/methods`)
            const list = response.data?.data?.methods
            if (Array.isArray(list)) {
                const known = list.filter((m) => KNOWN_PAYMENT_METHODS.includes(m))
                if (known.length > 0) methods = known
            }
        } catch (error) {
            // keep the default
        }
        dispatch(paymentMethodsLoaded(methods))
        return methods
    }
}

export default creditPackagesSlice.reducer
